import { Loading } from "components";
import type { FC } from "react";
import { useEffect } from "react";

import { Lightbox } from "components/Lightbox";

import { useCheckNewAssignmentsQuery } from "api/generated";
import { useAppSelector } from "store/hooks";
import { getGrade } from "store/slices/user";
import { processApiError } from "utils/processApiError";

interface Props {
  isShown: boolean;
  onHide: () => void;
}

export const NewAssignmentsModal: FC<Props> = ({ isShown, onHide }) => {
  const { data, isLoading, error } = useCheckNewAssignmentsQuery();
  const grade = useAppSelector(getGrade);
  const isGraduate = grade === "maturant";

  useEffect(() => {
    if (error === undefined) return;

    processApiError(error);
  }, [error]);

  const getTestName = ({ testType, description }: { testType: string; description: string }) => {
    if (testType === "todas") {
      return (
        <>
          <strong className="text-purple">To dáš</strong>: {description}
        </>
      );
    }

    return `Cermat: ${description}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("cs-CZ", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  };

  return (
    // Krátký otázkový titulek bez ilustrace patří do hlavičky — stejný vzor jako
    // `EnvelopeExplanation` (shodné `pt-0 px-32px` na těle). [TD-344]
    <Lightbox
      show={isShown}
      onHide={onHide}
      className="modal--400"
      bodyClassName={"pt-0 px-32px"}
      title="Kdy přibydou další úlohy?"
      footer={
        <button className="btn btn-no-border w-100" onClick={onHide}>
          Zavřít
        </button>
      }
    >
      <p>
        Nové úlohy přibydou{" "}
        <strong>vždy poté, co absolvuješ {isGraduate ? "maturitu nanečisto" : "přijímačky nanečisto"}</strong>. Čím víc
        testů absolvuješ, tím víc příkladů tu budeš k trénování mít. Průběžně také nahráváme úlohy z CERMAT testů podle
        harmonogramu níže. <strong>U distančních termínů nezapomeň</strong> po testu k úlohám v zóně zadat body, ať se
        správně rozřadí do obálek a nemáš je všechny v červené
      </p>

      {isLoading ? (
        <Loading />
      ) : (
        <table className="table-announcements">
          <tr>
            <th>Název testu</th>
            <th>Datum přidání do aplikace</th>
          </tr>
          {grade === "9" && (
            <tr>
              <td colSpan={2} className="text-center">
                <strong className="text-purple">To dáš testy</strong>: Po každém absolvovaném termínu
              </td>
            </tr>
          )}
          {data?.displayNewAssignmentAnnouncements.map((announcement, index) => {
            if (announcement.date === null) return null;

            return (
              <tr key={`${index}-${announcement.testType}`}>
                <td>{getTestName(announcement)}</td>
                <td>{formatDate(announcement.date)}</td>
              </tr>
            );
          })}
        </table>
      )}
    </Lightbox>
  );
};
